import {
  Button,
  Checkbox,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from "@chakra-ui/react";
import { Global } from "@emotion/react";
import React from "react";
import { RiArrowDownSLine } from "react-icons/ri";
import { RemoteDataTable } from "src/components/Table/RemoteDataTable";
import { rowExpanderColumnDef } from "src/components/Table/rowExpanderColumnDef";
import { ColumnDefWithTooltip } from "src/components/Table/Table";
import * as FSQ from "src/generated/hooks/form-service/queries";
import { SchoolCapacitySummary } from "src/generated/hooks/form-service/requests";
import * as RD from "src/types/remoteData";

type Props = {
  enrollmentPeriodId: string;
};
type Grade = { id: string; name: string };

export const BirdsEyeViewTable: React.FC<Props> = ({ enrollmentPeriodId }) => {
  const allSchoolsQuery = FSQ.useGetCapacitySummaryByEnrollmentPeriod(
    {
      path: {
        enrollmentPeriodId,
      },
    },
    [enrollmentPeriodId]
  );
  const allSchoolsRD = RD.useFromRestResultMemoized(allSchoolsQuery);
  const remoteData = React.useMemo(() => {
    return allSchoolsRD.map((data) => data.data.capacities);
  }, [allSchoolsRD]);

  const grades: Grade[] = React.useMemo(() => {
    return remoteData
      .map((capacities) => {
        return capacities[0]?.grades ?? [];
      })
      .withDefault([]);
  }, [remoteData]);

  const columns: ColumnDefWithTooltip<SchoolCapacitySummary>[] = React.useMemo(
    () => [
      rowExpanderColumnDef({
        header: "School",
        cell: ({ row }) => {
          return (
            <Flex fontStyle={row.depth > 0 ? "italic" : undefined}>
              {row.original.name}
            </Flex>
          );
        },
      }),
      ...grades.map((grade) => toGradeColumn(grade)),
      {
        id: "totalCapacity",
        header: "Total capacity",
        meta: { className: TOTAL_CAPACITY_CLASS },
        cell: ({ row }) => {
          return (
            <Flex
              fontStyle={row.depth > 0 ? "italic" : undefined}
              justifyContent="center"
            >
              {row.original.totalCapacity}
            </Flex>
          );
        },
      },
      {
        id: "seatsAvailable",
        header: "Available seats",
        meta: { className: SEATS_AVAILABLE_CLASS },
        cell: ({ row }) => {
          return (
            <Flex
              fontStyle={row.depth > 0 ? "italic" : undefined}
              justifyContent="center"
            >
              {row.original.availableSeats}
            </Flex>
          );
        },
      },
    ],
    [grades]
  );

  return (
    <Flex direction="column" gap="2">
      <ColumnSelector />
      <RemoteDataTable<SchoolCapacitySummary>
        data={remoteData}
        columns={columns}
        getSubRows={(row) => row.programs}
        tableContainerProps={{
          paddingTop: "0",
          paddingRight: "0",
          paddingBottom: "0",
          className: TABLE_CLASS,
        }}
        expandAllProps={{
          hideExpandAll: true,
        }}
        hideItemCount
        getCellStyleProps={(cell) => {
          const header = cell.column.columnDef.header;
          if (header === "School") {
            return {};
          }
          return {
            borderLeft: "1px solid",
            borderColor: "gray.200",
            paddingX: "0.5rem",
          };
        }}
        getHeaderStyleProps={(cell) => {
          const header = cell.column.columnDef.header;
          if (header === "School") {
            return { textAlign: "left", paddingLeft: "10" };
          }

          return {
            ...(header === "TOT" || header === "AVL"
              ? {
                  borderTop: "1px solid",
                }
              : {}),
            borderLeft: "1px solid",
            borderColor: "gray.200",
            textAlign: "center",
            paddingX: "0.5rem",
          };
        }}
      />
    </Flex>
  );
};

function toGradeColumn(
  grade: Grade
): ColumnDefWithTooltip<SchoolCapacitySummary> {
  const subColumns: ColumnDefWithTooltip<SchoolCapacitySummary>[] = [
    {
      id: `${grade.id}.TOT`,
      header: "TOT",
      meta: { className: GRADE_TOTAL_CAPACITY_CLASS },
      cell: ({ row }) => {
        return (
          <Flex
            fontStyle={row.depth > 0 ? "italic" : undefined}
            justifyContent="center"
          >
            {row.original.grades.find((g) => g.id === grade.id)
              ?.totalCapacity ?? ""}
          </Flex>
        );
      },
    },
    {
      id: `${grade.id}.AVL`,
      header: "AVL",
      meta: { className: GRADE_SEATS_AVAILABLE_CLASS },
      cell: ({ row }) => {
        return (
          <Flex
            fontStyle={row.depth > 0 ? "italic" : undefined}
            justifyContent="center"
          >
            {row.original.grades.find((g) => g.id === grade.id)
              ?.availableSeats ?? ""}
          </Flex>
        );
      },
    },
  ];

  return {
    id: grade.id,
    header: grade.name,
    meta: { className: GRADE_CLASS },
    columns: subColumns,
  };
}

const TABLE_CLASS = "birds-eye-view-table";
const TOTAL_CAPACITY_CLASS = "col-total-capacity";
const SEATS_AVAILABLE_CLASS = "col-available";
const GRADE_TOTAL_CAPACITY_CLASS = "col-grade-total-capacity";
const GRADE_SEATS_AVAILABLE_CLASS = "col-grade--available";
const GRADE_CLASS = "col-grade";

/**
 * We are using CSS to show/hide column to get around performance issue when using react
 * for showing/hiding column because it requires full table re-render, and it can be costly
 * when the table is significantly large.
 *
 * Tested on ~200 rows with 28 columns, it takes around 600ms to re-render the table, which is noticable,
 * while the CSS solution feels very instantinous.
 *
 * On top of using CSS, we also modify the colSpan of the grade column to flip between colSpan: 1 and colSpan: 2
 * using javascript to modify the DOM element directly.
 */
const ColumnSelector = () => {
  const [showTotalCapacity, setShowTotalCapacity] = React.useState(true);
  const [showAvailableSeats, setShowAvailableSeats] = React.useState(true);

  const handleTotalCapacityChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setShowTotalCapacity(e.target.checked);
      const gradeTableHeaderColumns = document.querySelectorAll(
        `th.${GRADE_CLASS}`
      );

      // Modify the colSpan to match the number of sub columns.
      gradeTableHeaderColumns.forEach((th) => {
        if (isTableHeaderCell(th)) {
          th.colSpan = e.target.checked ? 2 : 1;
        }
      });
    },
    []
  );

  const handleAvailableSeatsChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setShowAvailableSeats(e.target.checked);
      const gradeTableHeaderColumns = document.querySelectorAll(
        `th.${GRADE_CLASS}`
      );

      // Modify the colSpan to match the number of sub columns.
      gradeTableHeaderColumns.forEach((th) => {
        if (isTableHeaderCell(th)) {
          th.colSpan = e.target.checked ? 2 : 1;
        }
      });
    },
    []
  );

  return (
    <Flex justifyContent="flex-end">
      <Global
        styles={{
          [`.${TABLE_CLASS} th`]: {
            height: showTotalCapacity && showAvailableSeats ? "2rem" : "4rem",
          },
          [`.${TABLE_CLASS} .${GRADE_TOTAL_CAPACITY_CLASS},.${TABLE_CLASS}  .${TOTAL_CAPACITY_CLASS}`]:
            {
              display: !showTotalCapacity ? "none" : undefined,
            },
          [`.${TABLE_CLASS} th.${GRADE_TOTAL_CAPACITY_CLASS}`]: {
            display: !showAvailableSeats ? "none" : undefined,
          },

          [`.${TABLE_CLASS} .${GRADE_SEATS_AVAILABLE_CLASS}, .${TABLE_CLASS} .${SEATS_AVAILABLE_CLASS}`]:
            {
              display: !showAvailableSeats ? "none" : undefined,
            },
          [`.${TABLE_CLASS} th.${GRADE_SEATS_AVAILABLE_CLASS}`]: {
            display: !showTotalCapacity ? "none" : undefined,
          },
        }}
      />
      <Menu>
        <MenuButton
          as={Button}
          rightIcon={<RiArrowDownSLine />}
          variant="banner"
          size="sm"
        >
          View columns
        </MenuButton>
        <MenuList>
          <MenuItem closeOnSelect={false}>
            <Checkbox
              isDisabled={!showAvailableSeats}
              isChecked={showTotalCapacity}
              onChange={handleTotalCapacityChange}
            >
              Total capacity
            </Checkbox>
          </MenuItem>
          <MenuItem closeOnSelect={false}>
            <Checkbox
              isDisabled={!showTotalCapacity}
              isChecked={showAvailableSeats}
              onChange={handleAvailableSeatsChange}
            >
              Available seats
            </Checkbox>
          </MenuItem>
        </MenuList>
      </Menu>
    </Flex>
  );
};
function isTableHeaderCell(element: Element): element is HTMLTableCellElement {
  return element.tagName === "TH";
}
